import { Box, Typography } from '@mui/material';
import JourneyNewCardForm from '~/components/JourneyPayment/JourneyNewCardForm';
import JourneyPaymentMethodSelector from '~/components/JourneyPayment/JourneyPaymentMethodSelector';
import { NEW_CARD_OPTION, type JourneyAddedCard, type JourneySavedCard } from '~/components/JourneyPayment/types';
import JourneyQuoteSummaryColumn, {
  type JourneyQuoteSubscriptionRow
} from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteSummaryColumn';
import { lookupRenewalZipcode } from '~/services/modules/renewal';
import type { BrazilAddressFields } from '~/utils/brazilAddress';
import { RENEWAL_APP_IDS } from '../renewalAppIds';
import { formatRenewalCurrency } from '../renewalReview';

type Props = {
  cards: JourneySavedCard[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCardAdded: (card: JourneyAddedCard) => void;
  /** Endereço do contrato, que pré-preenche o endereço de cobrança do cartão novo. */
  billingAddress: Partial<BrazilAddressFields>;
  summary: { rows: JourneyQuoteSubscriptionRow[]; totalMonthly: number; serviceCharge: number };
  /** «Pagar e finalizar» (CORE-996); sem ele o botão fica desabilitado. */
  onPay?: () => void;
  paying?: boolean;
  /** Chave pública do gateway da organização (buscada uma vez no hook da jornada). */
  publicKey: string | null;
  publicKeyLoading?: boolean;
};

/** Etapa «Escolha a forma de pagamento» da renovação (Figma 2091:3730, 2091:4107, 2091:4335). */
export default function RenewalPaymentStep({
  cards,
  selectedId,
  onSelect,
  onCardAdded,
  billingAddress,
  summary,
  onPay,
  paying = false,
  publicKey,
  publicKeyLoading = false
}: Props) {
  const newCardOpen = selectedId === NEW_CARD_OPTION;

  return (
    <Box
      data-app-id={RENEWAL_APP_IDS.stepPayment}
      data-testid="renewal-step-payment"
      sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}
    >
      <Box>
        <Typography component="h1" sx={{ color: 'text.primary' }} variant="h5">
          Escolha a forma de pagamento
        </Typography>
        <Typography sx={(theme) => ({ color: theme.customColors.textPlaceholder })} variant="subtitle1">
          A cobrança é mensal e recorrente no cartão de crédito escolhido.
        </Typography>
      </Box>

      <Box
        sx={{
          alignItems: 'start',
          display: 'grid',
          gap: '24px',
          gridTemplateColumns: { md: 'minmax(0, 1fr) minmax(0, 478px)', xs: 'minmax(0, 1fr)' }
        }}
      >
        <JourneyPaymentMethodSelector
          appId={RENEWAL_APP_IDS.paymentMethod}
          cards={cards}
          disabled={paying}
          newCardForm={
            <JourneyNewCardForm
              appId={RENEWAL_APP_IDS.newCard}
              defaultBillingAddress={billingAddress}
              lookupZipcode={lookupRenewalZipcode}
              onAdded={onCardAdded}
              publicKey={publicKey}
              publicKeyLoading={publicKeyLoading}
            />
          }
          onSelect={onSelect}
          selectedId={selectedId}
        />

        <Box data-app-id={RENEWAL_APP_IDS.paymentSummary} data-testid="renewal-payment-summary" sx={{ minWidth: 0 }}>
          <JourneyQuoteSummaryColumn
            advanceLabel="Pagar e finalizar"
            advancing={paying}
            disableAdvance={!onPay || !selectedId || newCardOpen || paying}
            membershipFee={0}
            onAdvance={() => onPay?.()}
            productsLoading={false}
            securityMessage="Seus dados estão protegidos e serão usados apenas para a renovação."
            serviceChargeFormatted={formatRenewalCurrency(summary.serviceCharge)}
            showAdvanceButton={!newCardOpen}
            showOperationalRow={summary.serviceCharge > 0}
            subscriptionRows={summary.rows}
            totalMonthlyNumber={summary.totalMonthly}
          />
        </Box>
      </Box>
    </Box>
  );
}
